import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Camera } from "lucide-react";
import api from "@/lib/api";

/**
 * Rencontres du Fil — horizontal frieze of photos sent by walkers who found the Fée.
 * Silently returns nothing if no approved photos exist yet.
 */
export default function RencontresFrieze() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/rencontres?limit=24")
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 1 }}
      className="mt-24 md:mt-32 border-t border-fg/15 pt-14 md:pt-20"
      data-testid="rencontres-frieze"
    >
      <div className="max-w-[720px]">
        <div className="meta-label text-cinabre flex items-center gap-2">
          <Camera size={13} strokeWidth={1.5} />
          § Les Rencontres du Fil · Une chronique visuelle collective
        </div>
        <h2 className="mt-4 font-serif font-light text-4xl md:text-5xl leading-[0.98] tracking-tight">
          Elle s'est laissée <em className="italic">trouver</em>.
        </h2>
        <p className="mt-5 font-serif italic text-lg md:text-xl leading-[1.5] text-fg/70">
          Photos envoyées par des promeneurs qui ont croisé la Fée au fil des saisons. Chacune raconte un matin, une lumière, une rencontre.
        </p>
      </div>

      {/* Horizontal frieze */}
      <div className="mt-10 -mx-6 md:-mx-12 overflow-x-auto no-scrollbar" data-testid="rencontres-scroll">
        <div className="flex gap-4 md:gap-6 px-6 md:px-12 pb-4">
          {items.map((r, i) => (
            <motion.figure
              key={r.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: (i % 10) * 0.04 }}
              className="flex-shrink-0 w-[280px] md:w-[360px] group"
              data-testid={`rencontre-card-${r.id}`}
            >
              <div className="aspect-[3/4] overflow-hidden border border-fg/15 bg-fg/5 relative">
                <img
                  src={r.image_url}
                  alt={r.location ? `La Fée du Canal — ${r.location}` : "La Fée du Canal — photographie envoyée par un promeneur"}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.03]"
                />
                {r.featured && (
                  <div className="absolute top-3 left-3 px-2 py-0.5 bg-cinabre text-bg font-mono text-[9px] tracking-[0.2em] uppercase">
                    § Coup de cœur
                  </div>
                )}
              </div>
              <figcaption className="mt-3">
                {r.taken_at && (
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/55">
                    § {r.taken_at}{r.location ? ` · ${r.location}` : ""}
                  </div>
                )}
                {r.message && (
                  <div className="mt-2 font-serif italic text-sm md:text-base leading-snug text-fg/80">
                    « {r.message} »
                  </div>
                )}
                {r.photographer_name && (
                  <div className="mt-1 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/55">
                    — {r.photographer_name}
                  </div>
                )}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>

      <p className="mt-8 font-serif italic text-sm md:text-base text-fg/60 max-w-2xl">
        Une photo à partager ? Écrivez-nous à <a href="mailto:arteurs.lecollectif@gmail.com?subject=Ma%20photo%20de%20la%20F%C3%A9e%20du%20Canal" className="text-cinabre border-b border-cinabre/40 hover:border-cinabre">arteurs.lecollectif@gmail.com</a>.
      </p>
    </motion.section>
  );
}
